"use client";

import { useId, useState, useTransition, type FormEvent } from "react";
import { useAction } from "convex/react";
import { CheckCircle2, Loader2 } from "@/components/icons";
import { api } from "@convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PublicConvexProvider } from "@/components/providers/public-convex-provider";
import { handleError } from "@/lib/handle-error";
import type { PriceExportScope } from "@shared/price-index/price-export";

type Outcome = { kind: "sent"; message: string } | { kind: "error"; message: string };

function useExportRequest(scope: PriceExportScope) {
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [isPending, startTransition] = useTransition();
  const emailPriceExport = useAction(api.priceIndex.exports.emailPriceExport);
  function request(email: string) {
    setOutcome(null);
    startTransition(async () => {
      try {
        const result = await emailPriceExport({ email, ...scope });
        setOutcome({ kind: result.success ? "sent" : "error", message: result.message });
      } catch (failure) {
        setOutcome({
          kind: "error",
          message:
            handleError(failure, { display: "silent" }).message ??
            "Something went wrong. Try again in a moment.",
        });
      }
    });
  }
  return { outcome, isPending, request };
}

function SentNotice({ message }: { message: string }) {
  return (
    <p role="status" className="flex items-center gap-2 text-sm text-success-strong">
      <CheckCircle2 className="size-4 shrink-0" aria-hidden />
      {message} Check your inbox for the spreadsheet.
    </p>
  );
}

function EmailExportForm({ scope }: { scope: PriceExportScope }) {
  const inputId = useId();
  const [email, setEmail] = useState("");
  const { outcome, isPending, request } = useExportRequest(scope);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    request(email);
  }

  if (outcome?.kind === "sent") return <SentNotice message={outcome.message} />;
  return (
    <form onSubmit={submit} className="grid gap-2">
      <label htmlFor={inputId} className="text-sm font-medium text-foreground">
        Work email
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id={inputId}
          type="email"
          required
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={isPending}
          className="min-w-0 flex-1"
        />
        <Button variant="primary" type="submit" disabled={isPending}>
          {isPending && <Loader2 className="animate-spin" aria-hidden />}
          Email me the spreadsheet
        </Button>
      </div>
      {outcome?.kind === "error" && (
        <p role="alert" className="text-sm text-destructive-strong">
          {outcome.message}
        </p>
      )}
      <p className="text-xs text-fg-muted">
        We also email a link to confirm the weekly price report for this
        material.
      </p>
    </form>
  );
}

export function PriceDataSection({
  scope,
  itemName,
  placeName,
  csvPath,
}: {
  scope: PriceExportScope;
  itemName: string;
  placeName: string;
  csvPath: string;
}) {
  return (
    <Card padding="roomy" id="price-data" className="scroll-mt-24">
      <div className="grid gap-6 md:grid-cols-2 md:items-start">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Take the data with you
          </h2>
          <p className="mt-1 max-w-[52ch] text-sm text-fg-muted">
            Monthly {itemName.toLowerCase()} prices for {placeName} since 2005,
            by grade, as a spreadsheet.
          </p>
          <p className="mt-3 text-sm">
            <a
              href={csvPath}
              rel="nofollow"
              className="font-medium text-action hover:text-action-hover hover:underline"
            >
              Signed in? Download it now
            </a>
          </p>
        </div>
        <PublicConvexProvider>
          <EmailExportForm scope={scope} />
        </PublicConvexProvider>
      </div>
    </Card>
  );
}
